import React from 'react';
import { ClientCard } from './ClientCard';
import { DraftClientCard } from './DraftClientCard';
import { ClientDashboardItem, ClientDraftListItem } from '@/types/client';
import { PaginationInfo } from '@/types/common';
import PaginationControls from '@/components/shared/PaginationControls';
import { CircularProgress } from '@mui/material';
import { ClientListTable } from '@/clients/list/components/ClientListTable';
import { ClientsEmptyState } from '@/clients/list/components/ClientsEmptyState';
import { ClientsTabEmptyState, type ClientsListStatus } from '@/clients/list/components/ClientsTabEmptyState';

interface ClientGridProps {
  clients: ClientDashboardItem[];
  hasAnyClients: boolean;
  clientStatus: ClientsListStatus;
  hasActiveFilters: boolean;
  statusTotals: {
    all?: number;
    active: number;
    suspended: number;
    drafts?: number;
    pendingDeletion?: number;
  };
  onAddClient: () => void;
  onReactivateClient?: (clientId: string) => void;
  onSuspendClient?: (clientId: string) => void;
  onDeleteClient?: (client: ClientDashboardItem) => void;
  currentStatus: "active" | "suspended" | "draft" | "pending_deletion";
  pagination: PaginationInfo;
  currentPage: number;
  listLoading?: boolean;
  onPageChange: (page: number) => void;
  onCardClick?: (clientId: string) => void;
  onOpenPanel?: (clientId: string) => void;
  onScheduleClient?: (client: ClientDashboardItem) => void;
  /** Continue-setup click for a draft card. Drafts always render as cards, regardless of viewMode. */
  onDraftClick?: (draftId: string) => void;
  onDeleteDraft?: (draftId: string) => void;
  deletingDraftId?: string | null;
  viewMode?: 'grid' | 'list';
  highlightedClientId?: string | null;
}

export const ClientGrid = ({
  clients,
  hasAnyClients,
  clientStatus,
  hasActiveFilters,
  statusTotals,
  onAddClient,
  onReactivateClient,
  onSuspendClient,
  onDeleteClient,
  pagination,
  currentPage,
  listLoading = false,
  onPageChange,
  onCardClick,
  onOpenPanel,
  onScheduleClient,
  onDraftClick,
  onDeleteDraft,
  deletingDraftId = null,
  viewMode = 'grid',
  highlightedClientId = null,
}: ClientGridProps) => {
  const isDraftsTab = clientStatus === 'draft';
  const hasClients = clients.length > 0;
  const showLoadingState = listLoading;
  const showPagination = Boolean(pagination && pagination.pages > 1) && !listLoading;
  const paginationSummary = pagination
    ? `Showing ${(currentPage - 1) * pagination.limit + 1}-${Math.min(
        currentPage * pagination.limit,
        pagination.total
      )} of ${pagination.total} clients`
    : '';

  const renderPaginationControls = (layout: 'stacked' | 'footer' = 'stacked') => {
    if (!showPagination) return null;

    return (
      <PaginationControls
        currentPage={currentPage}
        totalPages={pagination.pages}
        totalCount={pagination.total}
        pageSize={pagination.limit}
        onPageChange={onPageChange}
        isLoading={listLoading}
        showSummary={true}
        summaryText={paginationSummary}
        layout={layout}
      />
    );
  };
  
  return (
    <div className="relative">
      {showLoadingState ? (
        <div className="w-full flex flex-col items-center justify-center gap-3 py-10 bg-gray-50 rounded-lg">
          <CircularProgress size={26} sx={{ color: '#20AEDC' }} />
          <p className="text-sm text-gray-600">Loading clients...</p>
        </div>
      ) : hasClients ? (
        <>
          {isDraftsTab ? (
            <div
              id="clients-grid-container"
              className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 lg:gap-5 pb-2"
            >
              {(clients as unknown as ClientDraftListItem[]).map((draft) => (
                <DraftClientCard
                  key={draft.id}
                  draft={draft}
                  onContinue={(draftId) => onDraftClick?.(draftId)}
                  onDelete={onDeleteDraft}
                  isDeleting={deletingDraftId === draft.id}
                />
              ))}
            </div>
          ) : viewMode === 'list' ? (
            <ClientListTable
              clients={clients}
              onCardClick={onCardClick}
              onOpenPanel={onOpenPanel}
              onScheduleClient={onScheduleClient}
              onSuspendClient={onSuspendClient}
              onDraftClick={onDraftClick}
              onDeleteClient={onDeleteClient}
              highlightedClientId={highlightedClientId}
              footer={renderPaginationControls('footer')}
            />
          ) : (
            <div
              id="clients-grid-container"
              className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 lg:gap-5 pb-2"
            >
              {clients.map(client =>
                client.status === 'draft' ? (
                  <DraftClientCard
                    key={client.id}
                    draft={client as unknown as ClientDraftListItem}
                    onContinue={(draftId) => onDraftClick?.(draftId)}
                    onDelete={onDeleteDraft}
                    isDeleting={deletingDraftId === client.id}
                  />
                ) : (
                  <ClientCard
                    key={client.id}
                    client={client}
                    onReactivateClient={onReactivateClient}
                    onSuspendClient={onSuspendClient}
                    onScheduleClient={onScheduleClient}
                    onDeleteClient={onDeleteClient}
                    onClick={() => onCardClick?.(client.id)}
                    onOpenPanel={onOpenPanel}
                    highlighted={highlightedClientId === client.id}
                  />
                )
              )}
            </div>
          )}

          {/* Pagination */}
          {(isDraftsTab || viewMode !== 'list') && showPagination && (
            <div className="mt-6 overflow-hidden rounded-[10px] border-x border-b border-[#e3e8ee] bg-white">
              {renderPaginationControls('footer')}
            </div>
          )}
        </>
      ) : hasActiveFilters ? (
        <ClientsTabEmptyState
          clientStatus={clientStatus}
          hasActiveFilters={hasActiveFilters}
          statusTotals={statusTotals}
        />
      ) : !hasAnyClients ? (
        <ClientsEmptyState onAddClient={onAddClient} />
      ) : (
        <ClientsTabEmptyState
          clientStatus={clientStatus}
          hasActiveFilters={hasActiveFilters}
          statusTotals={statusTotals}
        />
      )}
    </div>
  );
};
